import { useMemo, useCallback, useState, useContext, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm, Controller, useWatch, type SubmitErrorHandler } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { GlobalContext } from 'context';
  import { listType } from 'common/utils';
import { addMonthsToISODate, getCurrentYearEndISODate, normalizeToISODate, toISODateString } from 'common/utils/DateFormat';
import {
  Headings,
  Paragraph,
  GlobalErrorAlert,
  InputField,
  Label,
  ErrorMessage,
  HelpIcon,
  Checkbox,
  Button,
  DatePicker,
  PrintIcon,
  ChevronLeftIcon,
  TextArea,
  Link,
  ModalMultiUse,
} from '@sentara-health/optimahealth_web';
import {
  DateFormatRegex,
  EmailRegex,
  getMaxLength,
  specialCharacterFormat,
  DeviceTypes,
  useDeviceType,
  NavigateConst,
  formatISODateToMDY,
  useProxyAuthorization,
  domainForSegment,
  getErrorTypeByCode,
  ANALYTICS_MESSAGES,
  API_STATUS_CONFLICT,
} from 'common';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { AdobeFormConfig } from 'common/AdobeFormConfig';
import type { ProxyContactPoint } from '@sentara-health/sentara-api-hooks-core';
interface FormDataProps {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  disclosure: DisclosureProps;
  expiration: ExpirationProps;
  date: string;
  purpose: PurposeProps;
  disclosureOtherText: string;
  abuseAuthorization: AbuseAuthorizationProps;
  acknowledgement: boolean;
}
interface DisclosureProps {
  medicalClaims: boolean;
  accountInfo: boolean;
  clinicalInfo: boolean;
  primaryPhysician: boolean;
  allAbove: boolean;
}
interface ExpirationProps {
  disenrollmentExp: boolean;
  customExp: boolean;
  date: string;
}
interface PurposeProps {
  disclosureRequest: boolean;
  disclosureOther: boolean;
  disclosureOtherText: string;
}
interface AbuseAuthorizationProps {
  substanceAbuseYes: boolean;
  substanceAbuseNo: boolean;
}

type CSRProxyRouteState = {
  readApiFailed?: boolean;
  updateLoadFailed?: boolean;
  repData?: {
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    claimsList?: string[];
    expirationStartRaw?: string;
    expirationEndRaw?: string;
    disclosureOption?: string;
    disclosureOptionDescription?: string;
    alcoholAbuseAuthorization?: string;
    designatorLoginId?: string;
    correlationId?: string;
    designateLoginId?: string;
  };
};

const getNameFromRepData = (repData?: CSRProxyRouteState['repData']) => {
  const firstName = repData?.firstName?.trim() ?? '';
  const lastName = repData?.lastName?.trim() ?? '';

  if (firstName || lastName) {
    return { firstName, lastName };
  }

  const fullName = repData?.name?.trim() ?? '';

  if (!fullName) {
    return { firstName: '', lastName: '' };
  }

  const [parsedFirstName, ...parsedLastName] = fullName.split(/\s+/);
  return {
    firstName: parsedFirstName ?? '',
    lastName: parsedLastName.join(' ').trim(),
  };
};

// Backend requires these disclosure values in English regardless of the selected UI language.
const DISCLOSURE_CLAIM_LABELS = {
  medicalClaims: 'Medical claims and Authorizations',
  accountInfo: 'Account Information',
  clinicalInfo: 'Clinical Information',
  primaryPhysician: 'Primary physician',
} as const;

// Reverse-map stored claim labels back to the disclosure checkbox state for the update form.
const mapClaimsToDisclosure = (
  claims: string[] = []
): DisclosureProps => {
  // Stored labels are always English; match against the English values loosely by casing/spacing.
  const normalizedClaims = claims.map((claim) => claim.trim().toLowerCase());
  const hasClaim = (label: string) => normalizedClaims.includes(label.trim().toLowerCase());
  const medicalClaims = hasClaim(DISCLOSURE_CLAIM_LABELS.medicalClaims);
  const accountInfo = hasClaim(DISCLOSURE_CLAIM_LABELS.accountInfo);
  const clinicalInfo = hasClaim(DISCLOSURE_CLAIM_LABELS.clinicalInfo);
  const primaryPhysician = hasClaim(DISCLOSURE_CLAIM_LABELS.primaryPhysician);

  return {
    medicalClaims,
    accountInfo,
    clinicalInfo,
    primaryPhysician,
    allAbove: medicalClaims && accountInfo && clinicalInfo && primaryPhysician,
  };
};

// Reverse-map the stored disclosure option back to the purpose radio state for the update form.
const mapDisclosureOptionToPurpose = (
  disclosureOption?: string,
  disclosureOptionDescription?: string
): PurposeProps => {
  const trimmedDescription = disclosureOptionDescription?.trim() ?? '';
  // Only select "Other" when there is actual custom content; otherwise default to "At my request".
  const isOther =
    (disclosureOption ?? '').trim().toLowerCase() === 'other' && trimmedDescription.length > 0;
  return {
    disclosureRequest: !isOther,
    disclosureOther: isOther,
    disclosureOtherText: isOther ? trimmedDescription : '',
  };
};

// Reverse-map the stored substance-use authorization back to the radio state for the update form.
const mapAuthorizationToAbuse = (authorization?: string): AbuseAuthorizationProps => {
  const normalized = (authorization ?? '').trim().toLowerCase();
  return {
    substanceAbuseYes: normalized === 'yes',
    substanceAbuseNo: normalized === 'no',
  };
};

// Custom expiration date is selectable only from 72 hours (3 days) out.
const EXPIRATION_MIN_LEAD_DAYS = 3;
const getMinExpirationDate = (): Date => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + EXPIRATION_MIN_LEAD_DAYS);
  return date;
};

// Local YYYY-MM-DD for the minimum expiration date (avoids the UTC shift of toISOString).
const getMinExpirationISODate = (): string => {
  const date = getMinExpirationDate();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const CSRProxyForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location?.state;
  const { loginMemberId, primaryUser, profileState, memberCoverageEndDate } = useContext(GlobalContext);
  const { initiateProxy, updateProxy } = useProxyAuthorization();
  const { formSubmissionSucceeded, formSubmissionFailed, contentListingItemClicked } = useAdobeAnalytics();

  const routeState = useMemo(
    () => (locationState ?? {}) as CSRProxyRouteState,
    [locationState]
  );

  // Name comes prefilled from the existing authorization in update mode and must not be edited.
  const isUpdateMode = useMemo(
    () => Boolean(routeState.repData?.correlationId),
    [routeState.repData?.correlationId]
  );

  // Preserve the original start date on update; the start date is only created when the DR form is first initiated.
  const existingConsentStart = useMemo(() => {
    const raw = routeState.repData?.expirationStartRaw?.trim() ?? '';
    if (!raw) {
      return '';
    }
    return raw.includes('T') ? raw.split('T')[0] : raw;
  }, [routeState.repData?.expirationStartRaw]);

  const hasIncompleteUpdatePrefill = useMemo(() => {
    if (!isUpdateMode) {
      return false;
    }

    const repData = routeState.repData;
    const { firstName, lastName } = getNameFromRepData(repData);

    return Boolean(
      routeState.readApiFailed ||
        routeState.updateLoadFailed ||
        !firstName ||
        !lastName ||
        !repData?.email?.trim() ||
        !repData?.claimsList?.length
    );
  }, [isUpdateMode, routeState]);

  const defaultValues = useMemo(() => {
    const { firstName, lastName } = getNameFromRepData(routeState.repData);
    const email = routeState.repData?.email?.trim() ?? '';
    const disclosure = mapClaimsToDisclosure(routeState.repData?.claimsList);
    const expirationEndRaw = routeState.repData?.expirationEndRaw?.trim() ?? '';
    const customExpirationDate = expirationEndRaw ? formatISODateToMDY(expirationEndRaw) : '';
    const hasCustomExpiration = Boolean(customExpirationDate);
    const purpose = mapDisclosureOptionToPurpose(
      routeState.repData?.disclosureOption,
      routeState.repData?.disclosureOptionDescription
    );
    const abuseAuthorization = mapAuthorizationToAbuse(routeState.repData?.alcoholAbuseAuthorization);

    return {
      firstName,
      lastName,
      email,
      confirmEmail: email,
      disclosure,
      expiration: {
        disenrollmentExp: !hasCustomExpiration,
        customExp: hasCustomExpiration,
        date: customExpirationDate,
      },
      date: '',
      purpose,
      disclosureOtherText: '',
      abuseAuthorization,
      acknowledgement: false,
    };
  }, [routeState.repData, t]);

  const {
    handleSubmit,
    register,
    unregister,
    setValue,
    clearErrors,
    getValues,
    trigger,
    control,
    formState: { errors, isSubmitting, submitCount },
  } = useForm<FormDataProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  //device type detection for responsive design of pdf viewer
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  //state to handle modals for acknowledgement and purpose of disclosure sections
  const [isAcknowledgementModalOpen, setIsAcknowledgementModal] = useState(false);
  const [isSubstanceUseRecordsModalOpen, setIsSubstanceUseRecordsModal] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalTitle, setErrorModalTitle] = useState('');
  const [errorModalMessage, setErrorModalMessage] = useState('');

  //fields to watch for validation before submission
  const expiration = useWatch({ control, name: 'expiration' });
  const purpose = useWatch({ control, name: 'purpose' });
  const abuseAuthorization = useWatch({ control, name: 'abuseAuthorization' });
  const acknowledgement = useWatch({ control, name: 'acknowledgement' });
  const email = useWatch({ control, name: 'email' });

  useEffect(() => {
    if (getValues('confirmEmail') !== '') {
      void trigger('confirmEmail');
    }
  }, [email, getValues, trigger]);

  // objects and arrays to generate form fields and options dynamically, also used for validation and error handling
  const formDataPersonal: Record<string, any> = useMemo(() => ({
    firstName: {
      id: 'firstName',
      colClass: 'col-md-6 col-12 mb-3',
      required: true,
      label: t('common.firstName'),
      maxLength: getMaxLength.name,
      error: errors.firstName,
      restrictInputValue: specialCharacterFormat,
    },
    lastName: {
      id: 'lastName',
      colClass: 'col-md-6 col-12 mb-3',
      required: true,
      label: t('common.lastName'),
      maxLength: getMaxLength.name,
      error: errors.lastName,
      restrictInputValue: specialCharacterFormat,
    },
  }), [errors.firstName, errors.lastName, t]);

  const formDataEmail: Record<string, any> = useMemo(() => ({
    email: {
      id: 'email',
      colClass: 'col-md-6 col-12 mb-3',
      required: true,
      label: t('common.emailAddress'),
      maxLength: getMaxLength.email,
      error: errors.email,
      restrictInputValue: EmailRegex,
    },
    confirmEmail: {
      id: 'confirmEmail',
      colClass: 'col-md-6 col-12 mb-3',
      required: true,
      label: t('common.emailAddressConfirmation'),
      maxLength: getMaxLength.email,
      error: errors.confirmEmail,
      restrictInputValue: EmailRegex,
    },
  }), [errors.email, errors.confirmEmail, t]);

  const formDataDisclosures = useMemo(() => ([
    {
      id: 'medicalClaims',
      colClass: 'col-md-6 col-12 mb-3',
      label: t('profile.designatedRepresentative.checkBoxOption1'),
    },
    {
      id: 'accountInfo',
      colClass: 'col-md-6 col-12 mb-3',
      label: t('profile.designatedRepresentative.checkBoxOption2'),
    },
    {
      id: 'clinicalInfo',
      colClass: 'col-md-6 col-12 mb-3',
      label: t('profile.designatedRepresentative.checkBoxOption3'),
    },
    {
      id: 'primaryPhysician',
      colClass: 'col-md-6 col-12 mb-3',
      label: t('profile.designatedRepresentative.checkBoxOption4'),
    },
    {
      id: 'allAbove',
      colClass: 'col-md-6 col-12 mb-3',
      label: t('profile.designatedRepresentative.checkBoxOption5'),
    },
  ]), [t]);

  const formDataExpiration = useMemo(() => ([
    {
      id: 'disenrollmentExp',
      colClass: 'd-flex align-items-top col-xl-6 col-12 mb-xl-0 mb-3',
      label: t('profile.designatedRepresentative.coverageEnds'),
    },
    {
      id: 'customExp',
      label: t('profile.designatedRepresentative.customExpirationDate'),
      colClass: 'd-flex align-items-top col-xl-6 col-12 mb-xl-0 mb-2',
    },
    {
      id: 'date',
      label: t('profile.designatedRepresentative.expirationDate'),
      required: true,
      placeholder: t('common.selectDate'),
      maxLength: getMaxLength.date,
      error: errors.expiration?.date,
      restrictInputValue: DateFormatRegex,
    },
  ]), [errors.expiration?.date, t]);


  const formDataPurpose = useMemo(() => ([
    {
      id: 'disclosureRequest',
      colClass: 'd-flex align-items-center col-xl-6 col-12 mb-xl-2 mb-3',
      label: t('profile.designatedRepresentative.atMyRequest'),
    },
    {
      id: 'disclosureOther',
      label: t('profile.designatedRepresentative.others'),
      colClass: 'd-flex align-items-center col-xl-6 col-12 mb-2',
    },
    {
      id: 'disclosureOtherText',
      label: t('profile.designatedRepresentative.otherReason'),
      required: true,
      placeholder: t('profile.designatedRepresentative.stateReason') ?? '',
      maxLength: getMaxLength.textArea,
      error: errors.purpose?.disclosureOtherText,
      restrictInputValue: undefined,
    },
  ]), [errors.purpose?.disclosureOtherText, t]);

  const formDataAbuseAuthorization = useMemo(() => ([
    {
      id: 'substanceAbuseYes',
      colClass: 'd-flex align-items-top col-12 mb-3',
      label:
        <Trans i18nKey="common.radioAgree"
          values={{ fieldName: t('common.authorizationForSubstanceUseRecords') }}
          components={{
            link1: <Link
              onClick={() => { setIsSubstanceUseRecordsModal(true) }}
              className="text-decoration-underline"
            />
          }} />,
      checked: false,
    },
    {
      id: 'substanceAbuseNo',
      label: t('profile.designatedRepresentative.noAuthorize'),
      colClass: 'd-flex align-items-top col-12 mb-md-3 mb-4',
      checked: false,
    },
  ]), [t]);

  const formDataModals = useMemo(() => ({
    purposeHelpModal: {
      id: 'helpModal',
      title: undefined,
      content: t('profile.designatedRepresentative.purposeHelpModalContent'),
      variant: 'default',
      iconClose: true, // to show close on modal body
      isMobile,
      actionOpen: () => isHelpModalOpen,
      actionClose: () => setIsHelpModalOpen(false),
    },
    substanceModal: {
      id: 'substanceModal',
      title: t('common.authorizationForSubstanceUseRecords'),
      content:
        <Paragraph
          level={3}
          text={<Trans
            i18nKey="profile.designatedRepresentative.authorizationPopupContent"
            />}
          className="lineBrake mb-0" />,
      variant: 'default',
      isMobile,
      iconClose: false,
      actionOpen: () => isSubstanceUseRecordsModalOpen,
      actionClose: () => setIsSubstanceUseRecordsModal(false),
    },
    acknowledgementModal: {
      id: 'acknowledgementModal',
      title: t('profile.designatedRepresentative.acknowledgementPopupTitle'),
      content: listType(t('profile.designatedRepresentative.acknowledgementModalContent'), 'lineBrake, decimalList'),
      variant: 'default',
      isMobile,
      iconClose: false,
      actionOpen: () => isAcknowledgementModalOpen,
      actionClose: () => setIsAcknowledgementModal(false),
    },
  }), [t, isAcknowledgementModalOpen, isMobile, isSubstanceUseRecordsModalOpen, isHelpModalOpen]);

  //field options to generate checkboxes and radio buttons dynamically, also used for validation and error handling
  const individualDisclosureFields = useMemo(
    () => formDataDisclosures.filter((field) => field.id !== 'allAbove'),
    [formDataDisclosures]
  );

  const expirationOptions = useMemo(() => formDataExpiration.reduce((options, field) => {
    if (field.id !== 'date') {
      options[field.id] = expiration?.[field.id as keyof ExpirationProps] ? true : false;
    }
    return options;
  }, {} as Record<string, boolean>), [expiration, formDataExpiration]);

  const purposeOptions = useMemo(() => formDataPurpose.reduce((options, field) => {
    if (field.id !== 'disclosureOtherText') {
      options[field.id] = purpose?.[field.id as keyof PurposeProps] ? true : false;
    }
    return options;
  }, {} as Record<string, boolean>), [purpose, formDataPurpose]);

  const abuseAuthorizationOptions = useMemo(() => formDataAbuseAuthorization.reduce((options, field) => {
    options[field.id] = abuseAuthorization?.[field.id as keyof AbuseAuthorizationProps] ? true : false;
    return options;
  }, {} as Record<string, boolean>), [abuseAuthorization, formDataAbuseAuthorization]);

  // functions to handle change events 
  function handleAllAboveSelection(isChecked: boolean) {
    const nextDisclosure = individualDisclosureFields.reduce((acc, option) => {
      acc[option.id as keyof DisclosureProps] = isChecked;
      return acc;
    }, {
      ...(getValues('disclosure') as DisclosureProps),
      allAbove: isChecked,
    } as DisclosureProps);

    setValue('disclosure' as keyof FormDataProps, nextDisclosure as any, {
      shouldValidate: true,
    });

    const hasAnyDisclosureSelected = Object.values(nextDisclosure).some((value) => value);
    if (hasAnyDisclosureSelected && errors.disclosure) {
      clearErrors('disclosure');
    }
  };

  function handleIndividualDisclosureChange(isChecked: boolean, fieldId: keyof DisclosureProps) {
    const currentDisclosure = getValues('disclosure') as DisclosureProps;
    const nextDisclosure = {
      ...currentDisclosure,
      [fieldId]: isChecked,
    } as DisclosureProps;

    const allChecked = individualDisclosureFields.every(
      (option) => Boolean(nextDisclosure[option.id as keyof DisclosureProps])
    );
    nextDisclosure.allAbove = allChecked;
    setValue('disclosure' as keyof FormDataProps, nextDisclosure as any, { shouldValidate: true });

    const hasAnyDisclosureSelected = Object.values(nextDisclosure).some((value) => value);
    if (hasAnyDisclosureSelected && errors.disclosure) {
      clearErrors('disclosure');
    }
  };

  const resetAbuseAuthorizationOptions = (selectedId: string) => {
    formDataAbuseAuthorization.forEach((option) => {
      if (option.id !== selectedId) {
        setValue(`abuseAuthorization.${option.id}` as keyof FormDataProps, false as any);
      }
    });
  };

  // edge cases handling for validation of dynamically generated fields based on the selection of checkboxes and radio buttons
  const edgeCaseForExpirationDate = useMemo(() => ((!expirationOptions.customExp && !expirationOptions.disenrollmentExp) || expirationOptions.customExp) && (!expiration?.date && (Object.values(purposeOptions).some((value) => value) || Object.values(abuseAuthorizationOptions).some((value) => value))), [abuseAuthorizationOptions, expiration?.date, expirationOptions.customExp, expirationOptions.disenrollmentExp, purposeOptions]);
  const edgeCaseForPurpose = useMemo(() => ((!purposeOptions.disclosureRequest && !purposeOptions.disclosureOther) || purposeOptions.disclosureOther) && !purpose?.disclosureOtherText && Object.values((abuseAuthorizationOptions)).some((value) => value), [abuseAuthorizationOptions, purpose?.disclosureOtherText, purposeOptions.disclosureOther, purposeOptions.disclosureRequest]);

  const topLevelErrorCount = useMemo(() => {
    const errorFlags = [
      Boolean(errors.firstName),
      Boolean(errors.lastName),
      Boolean(errors.email),
      Boolean(errors.confirmEmail),
      Boolean(errors.disclosure),
      Boolean(errors.expiration) || edgeCaseForExpirationDate,
      Boolean(errors.purpose) || edgeCaseForPurpose,
      Boolean(errors.abuseAuthorization),
      Boolean(errors.acknowledgement),
    ];

    return errorFlags.filter(Boolean).length;
  }, [
    edgeCaseForExpirationDate,
    edgeCaseForPurpose,
    errors.abuseAuthorization,
    errors.acknowledgement,
    errors.confirmEmail,
    errors.disclosure,
    errors.email,
    errors.expiration,
    errors.firstName,
    errors.lastName,
    errors.purpose,
  ]);

  const showValidationSummary = submitCount > 0 && topLevelErrorCount > 0;

  const mapDisclosureClaims = useCallback((disclosure: DisclosureProps): string[] => {
    if (disclosure.allAbove) {
      return [
        DISCLOSURE_CLAIM_LABELS.medicalClaims,
        DISCLOSURE_CLAIM_LABELS.accountInfo,
        DISCLOSURE_CLAIM_LABELS.clinicalInfo,
        DISCLOSURE_CLAIM_LABELS.primaryPhysician,
      ];
    }

    const claims: string[] = [];
    if (disclosure.medicalClaims) claims.push(DISCLOSURE_CLAIM_LABELS.medicalClaims);
    if (disclosure.accountInfo) claims.push(DISCLOSURE_CLAIM_LABELS.accountInfo);
    if (disclosure.clinicalInfo) claims.push(DISCLOSURE_CLAIM_LABELS.clinicalInfo);
    if (disclosure.primaryPhysician) claims.push(DISCLOSURE_CLAIM_LABELS.primaryPhysician);

    return claims.length ? claims : [DISCLOSURE_CLAIM_LABELS.medicalClaims];
  }, []);

  const mapDisclosureOption = useCallback((purposeValue: PurposeProps) => {
    if (purposeValue.disclosureOther) {
      return {
        disclosureOption: 'Other',
        disclosureOptionDescription: purposeValue.disclosureOtherText?.trim() ?? '',
      };
    }

    return {
      disclosureOption: 'At my request',
      disclosureOptionDescription: 'Full disclosure of member health information',
    };
  }, []);

  const mapAuthorization = useCallback((authorization: AbuseAuthorizationProps) => (
    authorization.substanceAbuseYes ? 'Yes' : 'No'
  ), []);

  const mapConsentPeriod = useCallback((expirationValue: ExpirationProps) => {
    // Keep the existing start date on update; only initiate creates a new start date.
    const start = existingConsentStart || toISODateString(new Date());

    if (expirationValue.disenrollmentExp) {
      // Add 12 months to the coverage end date, or to the current year-end when none is available.
      const base = memberCoverageEndDate ?? getCurrentYearEndISODate();
      const end = addMonthsToISODate(base, 12);
      if (!end) {
        return undefined;
      }
      return {
        start,
        end,
      };
    }

    if (!expirationValue.customExp || !expirationValue.date) {
      return undefined;
    }

    const normalizedEndDate = normalizeToISODate(expirationValue.date);

    if (!normalizedEndDate) {
      return undefined;
    }

    return {
      start,
      end: normalizedEndDate,
    };
  }, [memberCoverageEndDate, existingConsentStart]);

  const closeErrorModal = useCallback(() => {
    setShowErrorModal(false);
    setErrorModalTitle('');
    setErrorModalMessage('');
  }, []);

  const renderTopErrorBanner = useCallback(
    (title: string, message: string, testId: string, className: string) => (
      <div className={className} data-testid={testId}>
        <GlobalErrorAlert variant="alert">
          <Paragraph
            text={title}
            variant="bold"
            className="mb-1"
          />
          <Paragraph
            text={message}
            variant="regular"
            className="mb-0"
          />
        </GlobalErrorAlert>
      </div>
    ),
    []
  );

  //submit && cancel handlers
  const onInvalid = useCallback<SubmitErrorHandler<FormDataProps>>((formErrors) => {
    console.error('CSR proxy form validation failed', formErrors);
  }, []);

  const onSubmit = useCallback(async (data: FormDataProps) => {
    try {
      const designatorName = `${data.firstName} ${data.lastName}`.trim();
      const designatorMemberId = loginMemberId?.trim() || '';

      if (!designatorMemberId) {
        console.error('Missing loginMemberId. initiateProxy was not called.');
        return;
      }

      const claims = mapDisclosureClaims(data.disclosure);
      const { disclosureOption, disclosureOptionDescription } = mapDisclosureOption(data.purpose);
      const alcoholAbuseAuthorization = mapAuthorization(data.abuseAuthorization);
      const consentPeriod = mapConsentPeriod(data.expiration);
      const acknowledgement = data.acknowledgement ? 'Yes' : 'No';
      const proxyFullName = `${data.firstName} ${data.lastName}`.trim();
      const loggedInFirstNameFromProfile = profileState?.firstName?.trim() || '';
      const loggedInLastNameFromProfile = profileState?.lastName?.trim() || '';
      const loggedInUserName = (`${loggedInFirstNameFromProfile} ${loggedInLastNameFromProfile}`.trim() || (primaryUser || '').trim());
      const loggedInUserEmail = profileState?.email?.trim() || profileState?.emailConsent?.email?.trim() || '';
      const [loggedInFirstName, ...loggedInLastNameParts] = loggedInUserName.split(/\s+/);
      const requestHumanName = {
        use: 'official',
        text: loggedInUserName || designatorName,
        family: loggedInLastNameParts.join(' ').trim() || data.lastName.trim(),
        given: [loggedInFirstName || data.firstName.trim()],
      };
      const proxyIdentifier = {
        system: 'designatorMemberId',
        value: designatorMemberId,
      };
      const proxyHumanName = {
        use: 'official',
        text: proxyFullName,
        family: data.lastName.trim(),
        given: [data.firstName.trim()],
      };

      const contactPoint: ProxyContactPoint[] = loggedInUserEmail
        ? [
            {
              system: 'email',
              value: loggedInUserEmail,
              use: 'home',
              rank: 2,
            },
          ]
        : [];

      const proxyContactPoint: ProxyContactPoint[] = [
        {
          system: 'email',
          value: data.email.trim(),
          use: 'work',
          rank: 2,
        },
      ];

      const updateRepData = routeState.repData;
      const isUpdateMode = Boolean(updateRepData?.correlationId);

      if (isUpdateMode) {
        await updateProxy({
          designatorLoginId: updateRepData?.designatorLoginId,
          designatorMemberId,
          designateLoginId: updateRepData?.designateLoginId,
          correlationId: updateRepData?.correlationId,
          proxyContactPoint,
          claims,
          disclosureOption,
          disclosureOptionDescription,
          alcoholAbuseAuthorization,
          acknowledgement,
          consentPeriod,
        });

        formSubmissionSucceeded({
          segment: `${domainForSegment}${location.pathname}`,
          form: AdobeFormConfig['drProxySubmit'],
        });
        // Pop the form off history so browser back from profile returns to the dashboard, not the form.
        navigate(-1);
        return;
      }

      await initiateProxy({
        designatorMemberId,
        designatorName,
        personaType: 'caregiver',
        contactPoint,
        requestHumanName,
        proxyIdentifier,
        proxyHumanName,
        proxyContactPoint,
        claims,
        disclosureOption,
        disclosureOptionDescription,
        proxyAuthorizationRequestStatus: 'Pending',
        acknowledgement,
        alcoholAbuseAuthorization,
        consentPeriod,
      });

      formSubmissionSucceeded({
        segment: `${domainForSegment}${location.pathname}`,
        form: AdobeFormConfig['drProxySubmit'],
      });
      navigate(NavigateConst.csrProxySuccess, {
        replace: true,
        state: {
          representativeName: designatorName,
          representativeEmail: data.email.trim(),
        },
      });
    } catch (error) {
      const err = error as {
        errorCode?: string | number;
        status?: number;
        message?: string;
        errorDetails?: { detail?: string };
      };
      const errorCode = err?.errorCode ?? err?.status;
      formSubmissionFailed({
        segment: `${domainForSegment}${location.pathname}`,
        form: {
          ...AdobeFormConfig['drProxySubmit'],
          formFailReason: `API Error (${getErrorTypeByCode(
            errorCode?.toString()
          )}): ${err?.message ?? err?.errorDetails?.detail ?? 'unknown'}`,
        },
      });
      console.error('Failed to initiate proxy:', error);
      const isConflict = errorCode?.toString() === API_STATUS_CONFLICT.toString();
      setErrorModalTitle(
        t(isConflict
          ? 'profile.designatedRepresentative.drFormErrorTitle'
          : 'profile.designatedRepresentative.apiFailureTitle') || ''
      );
      setErrorModalMessage(
        t(isConflict
          ? 'profile.designatedRepresentative.drFormErrorDescription'
          : 'profile.designatedRepresentative.apiFailureMessage') || ''
      );
      setShowErrorModal(true);
    }
  }, [formSubmissionFailed, formSubmissionSucceeded, initiateProxy, location.pathname, loginMemberId, mapAuthorization, mapConsentPeriod, mapDisclosureClaims, mapDisclosureOption, navigate, primaryUser, profileState?.email, profileState?.emailConsent?.email, profileState?.firstName, profileState?.lastName, routeState.repData, t, updateProxy]);

  const handleCancel = useCallback(() => {
    contentListingItemClicked({
      segment: `${domainForSegment}${location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [{ ...ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_CANCEL_CLICK }],
        },
      },
    });
    navigate(-1);
  }, [navigate, contentListingItemClicked, location.pathname]);

  const handlePrint = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    globalThis.print();
  }, []);

  return (
    <>
      {/* buttons to go back to profile and print the form, also used for responsive design of pdf viewer */}
      {hasIncompleteUpdatePrefill ? renderTopErrorBanner(
        t('profile.designatedRepresentative.updateLoadWarningTitle'),
        t('profile.designatedRepresentative.updateLoadWarningMessage'),
        'csrProxyUpdateWarningBanner',
        'col-12 mb-4'
      ) : null}
      <div className="d-flex justify-content-between align-items-center col-12 my-md-2 not-print">
        <Button
          data-testid="back-to-Authorization"
          iconLeft={<ChevronLeftIcon />}
          variant="icon_btn"
          onClick={handleCancel}
          className="ml-n1"
        >
          {t('common.back')}
        </Button>
        <Button
          data-testid="back-to-Authorization"
          iconLeft={<PrintIcon />}
          variant="icon_btn"
          onClick={handlePrint}
        >
          {t('common.print')}
        </Button>
      </div>
      {/* heading and description section of the form */}
      <div className="col-12 my-md-4 my-3">
        <Headings
          level={isMobile ? 2 : 1}
          text={t('profile.designatedRepresentative.designatedRepresentativeTab')}
          variant="semiBold"
          className="pt-3"
        />
        {isMobile ? null : <hr className="hr-1" />}
      </div>
      {showValidationSummary ? renderTopErrorBanner(
        t(
          topLevelErrorCount === 1
            ? 'profile.designatedRepresentative.singleFormErrorTitle'
            : 'profile.designatedRepresentative.formErrorTitle',
          { count: topLevelErrorCount }
        ),
        t('profile.designatedRepresentative.summaryMessage'),
        'csrProxyValidationSummaryBanner',
        'col-xl-7 col-lg-8 col-12 px-md-4 mb-4'
      ) : null}
      <div className="col-xl-7 col-lg-8 col-12 px-md-4 mt-md-5 mt-4 pt-md-0 pt-2">
        <Paragraph
          level={5}
          variant="bold"
          text={t('profile.designatedRepresentative.formTitle')}
          className="mb-2"
        />
        <Paragraph
          text={t('profile.designatedRepresentative.formDescription')}
          variant="regular"
          className="mb-md-3 mb-0"
        />
        <hr className="hr-2" />
      </div>
      {/* form section, generated dynamically based on the objects and arrays defined above */}
      <div id="printFormSection"
        className="col-xl-7 col-lg-10 col-12 px-md-4">
        <Paragraph
          level={3}
          text={<Trans i18nKey="profile.designatedRepresentative.allFieldsRequired" components={{ em: <em /> }} />}
          variant="italic"
        />
        {/* form sections generated dynamically based on the objects and arrays defined above */}
        <form onSubmit={handleSubmit(onSubmit, onInvalid)} data-testid="csrProxyForm">
          {/* personal information section */}
          <div className="row mb-4">
            <div className="col-12">
              <Paragraph
                level={5}
                variant="bold"
                text={t('profile.designatedRepresentative.designatedRepInfo')}
                className="mb-2"
              />
            </div>
            <div className="col-12">
              <div className="row">
                {Object.keys(formDataPersonal).map((key) => {
                  const field = formDataPersonal[key];
                  return (
                    <div className={field.colClass} key={field.id}>
                      <Label
                        htmlFor={field.id}
                        variant="formLabel"
                        className="mb-1"
                        data-testid={`${field.id}Label`}
                      >
                        {field.label}
                      </Label>
                      <div className="d-flex align-items-center">
                        <InputField
                          id={field.id}
                          type="text"
                          placeholder={t('common.placeholder', { fieldName: field.label.toLowerCase() })}
                          maxLength={field.maxLength}
                          data-testid={`${field.id}Input`}
                          error={field.error}
                          disabled={isUpdateMode}
                          className={field.error ? 'error-border' : ''}
                          {...register(field.id as keyof FormDataProps, {
                            required:
                              field.id === 'firstName'
                                ? t('profile.designatedRepresentative.firstNameRequired')
                                : t('profile.designatedRepresentative.lastNameRequired'),
                            pattern: {
                              value: field.restrictInputValue,
                              message:
                                field.id === 'firstName'
                                  ? t('profile.designatedRepresentative.firstNameRequired')
                                  : t('profile.designatedRepresentative.lastNameRequired'),
                            },
                            onChange: (e) => {
                              clearErrors(field.id as keyof FormDataProps);
                              setValue(field.id as keyof FormDataProps, e.target.value, {
                                shouldValidate: true,
                              });
                            },
                            validate: field.id
                              ? (value) => {
                                return field.restrictInputValue.test(String(value)) ||
                                  (field.id === 'firstName'
                                    ? t('profile.designatedRepresentative.firstNameRequired')
                                    : t('profile.designatedRepresentative.lastNameRequired'));
                              }
                              : undefined,
                          })}
                        />
                      </div>
                      {field.error ? (
                        <ErrorMessage
                          data-testid={`${field.id}Error`}
                          errors={errors}
                        >{field?.error?.message}</ErrorMessage>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <hr className="my-4" />
          {/* email section */}
          <div className="row mb-4">
            <div className="col-12">
              <Paragraph
                level={5}
                variant="bold"
                text={t('common.emailAddressConfirmation')}
                className="mb-2"
              />
            </div>
            <div className="col-12">
              <div className="row">
                {Object.keys(formDataEmail).map((key) => {
                  const field = formDataEmail[key];
                  return (
                    <div className={field.colClass} key={field.id}>
                      <Label
                        htmlFor={field.id}
                        variant="formLabel"
                        className="mb-1"
                        data-testid={`${field.id}Label`}
                      >
                        {field.label}
                      </Label>
                      <div className="d-flex align-items-center">
                        <InputField
                          id={field.id}
                          type="text"
                          placeholder={t('common.placeholder', { fieldName: field.label.toLowerCase() })}
                          maxLength={field.maxLength}
                          data-testid={`${field.id}Input`}
                          error={field.error}
                          className={field.error ? 'error-border' : ''}
                          {...register(field.id as keyof FormDataProps, {
                            required:
                              field.id === 'confirmEmail'
                                ? t('common.emailMismatch')
                                : t('profile.designatedRepresentative.emailInvalid'),
                            onChange: (e) => {
                              clearErrors(field.id as keyof FormDataProps);
                              setValue(field.id as keyof FormDataProps, e.target.value, {
                                shouldValidate: true,
                              });
                            },
                            validate: field.id === 'confirmEmail'
                              ? (value) => {
                                const emailValue = getValues('email');
                                if (value !== emailValue) {
                                  return t('common.emailMismatch');
                                }

                                if (!field.restrictInputValue.test(String(value))) {
                                  return t('profile.designatedRepresentative.emailInvalid');
                                }

                                return true;
                              }
                              : field.id
                                ? (value) => {
                                  if (!field.restrictInputValue.test(String(value))) {
                                    return t('profile.designatedRepresentative.emailInvalid');
                                  }

                                  const loggedInUserEmail = (profileState?.email || profileState?.emailConsent?.email || '').trim().toLowerCase();
                                  const enteredEmail = String(value).trim().toLowerCase();
                                  if (loggedInUserEmail && enteredEmail === loggedInUserEmail) {
                                    return t('common.emailIdValid');
                                  }

                                  return true;
                                }
                                : undefined,
                          })}
                        />
                      </div>
                      {field.error ? (
                        <ErrorMessage
                          data-testid={`${field.id}Error`}
                          errors={errors}
                        >{field?.error?.message}</ErrorMessage>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <hr className="my-4" />
          {/* disclosure section */}
          <div className="row mb-4">
            <div className="col-12">
              <Paragraph
                level={5}
                variant="bold"
                text={t('profile.designatedRepresentative.informationToDisclose')}
                className="my-2"
              />
              <Paragraph
                text={t('profile.designatedRepresentative.disclosureDescription')}
                variant="regular"
              />
            </div>
            <div className="col-12">
              <div className="row d-flex align-items-top">
                {formDataDisclosures.map((field) => (
                  <div className={`${field.colClass} d-flex align-items-center`} key={field.id}>
                    <Controller
                      name={`disclosure.${field.id}` as keyof FormDataProps}
                      control={control}
                      rules={{
                        validate: () => {
                          const currentDisclosure = getValues('disclosure') as DisclosureProps;
                          const hasAnyDisclosureSelected = Object.values(currentDisclosure).some((value) => value);
                          return hasAnyDisclosureSelected
                            || t('common.requiredSelection', { fieldName: t('profile.designatedRepresentative.informationToDisclose').toLowerCase() });
                        },
                      }}
                      render={({ field: { value, onBlur } }) => (
                        <Checkbox
                          {...field}
                          level={3}
                          id={field.id}
                          data-testid={`${field.id}Checkbox`}
                          checked={value || false}
                          onBlur={onBlur}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const isChecked = (e.target as HTMLInputElement).checked;
                            if (field.id === 'allAbove') {
                              handleAllAboveSelection(isChecked);
                            } else {
                              handleIndividualDisclosureChange(isChecked, field.id as keyof DisclosureProps);
                            }
                          }}
                        >
                          {field.label}
                        </Checkbox>
                      )}
                    />
                  </div>
                ))}
                {errors.disclosure ? (
                  <div className="col-12 mt-n1">
                    <ErrorMessage
                      data-testid={`disclosureError`}
                      errors={errors}
                    >
                      {t('profile.designatedRepresentative.informationDiscloseError', { fieldName: t('profile.designatedRepresentative.informationToDisclose').toLowerCase() })}
                    </ErrorMessage>
                  </div>
                ) : null}

              </div>
            </div>
          </div>
          <hr className="my-4" />
          {/* expiration date section */}
          <div className="row mb-4">
            <div className="col-12">
              <Paragraph
                level={5}
                variant="bold"
                text={t('profile.designatedRepresentative.expirationDate')}
                className="mt-2"
              />
            </div>
            <div className="col-12">
              <div className="d-flex row align-items-top">
                {formDataExpiration.map((field) => (
                  <div className={field.colClass} key={field.id}>
                    <Controller
                      name={`expiration.${field.id}` as keyof FormDataProps}
                      control={control}
                      rules={{
                        required: field.id === 'date'
                          ? expirationOptions.customExp ? t('common.requiredField', { fieldName: field.label.toLowerCase() }) : false
                          : !Object.values(expirationOptions).some((value) => value) ? t('common.requiredSelection', { fieldName: t('profile.designatedRepresentative.expirationDate').toLowerCase() }) : undefined,
                        pattern: field.restrictInputValue ? {
                          value: field.restrictInputValue,
                          message: t('common.validField', { fieldName: field.label.toLowerCase(), fieldFormat: t('common.dateFormat') }),
                        } : undefined,

                        validate: (value) => {
                          const hasSelection = expirationOptions.disenrollmentExp || expirationOptions.customExp;
                          if (!hasSelection) {
                            return t('common.requiredSelection', { fieldName: t('profile.designatedRepresentative.expirationDate').toLowerCase() });
                          }
                          if (field.id === 'date' && expirationOptions.customExp && value) {
                            const valueISO = normalizeToISODate(value as string);
                            if (valueISO && valueISO < getMinExpirationISODate()) {
                              return t('common.validField', { fieldName: field.label.toLowerCase(), fieldFormat: t('common.dateFormat') });
                            }
                          }
                          return true;
                        }
                      }}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <>
                          {field.id === 'date' ?
                            (
                              expirationOptions.customExp ? (
                                <div className="offset-xl-6 flex-1 col-md-6 col-12 csrProxy-cust-1">
                                  <DatePicker
                                    selectedValue={value}
                                    maxLength={field.maxLength}
                                    fieldName={field.id}
                                    minDate={getMinExpirationDate()}
                                    defaultActiveStartDate={getMinExpirationDate()}
                                    data-testid={`${field.id}Input`}
                                    placeholder={field.placeholder}
                                    updateValue={(updateValue: string) => {
                                      setValue(`expiration.${field.id}` as keyof FormDataProps, updateValue, {
                                        shouldValidate: true,
                                      });
                                      clearErrors(`expiration.${field.id}` as keyof FormDataProps);
                                    }}
                                    errorClass={errors.expiration?.[field.id as keyof ExpirationProps] ? 'error-border' : ''}
                                  />
                                </div>
                              ) : null
                            )
                            : (
                              <Checkbox
                                {...field}
                                level={3}
                                id={field.id}
                                type="radio"
                                data-testid={`${field.id}Checkbox`}
                                checked={value || false}
                                onBlur={onBlur}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const isChecked = (e.target as HTMLInputElement).checked;
                                  onChange(isChecked);
                                  if (isChecked) {
                                    formDataExpiration.forEach((option) => {
                                      if (option.id !== field.id && option.id !== 'date') {
                                        setValue(`expiration.${option.id}` as keyof FormDataProps, false as any);
                                      }
                                    });
                                  }
                                  clearErrors('expiration');
                                  unregister(`expiration.date` as keyof FormDataProps);
                                }}
                              >
                                {field.label}
                              </Checkbox>
                            )}
                        </>
                      )}
                    />
                  </div>
                ))}
                {errors.expiration || edgeCaseForExpirationDate ? (
                  <div className={`col-12 ${expirationOptions.customExp ? 'offset-xl-6 col-xl-6 csrProxy-cust-2' : 'mt-1'}`}>
                    <ErrorMessage
                      data-testid={`expirationError`}
                      errors={errors}                    >
                      {errors.expiration?.date?.message ? errors.expiration.date.message : edgeCaseForExpirationDate ? t('common.requiredSelection', { fieldName: t('profile.designatedRepresentative.expirationDate').toLowerCase() }) : ''}
                    </ErrorMessage>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <hr className="my-4" />
          {/* purpose of disclosure section */}
          <div className="row mb-4">
            <div className="col-12 d-flex align-items-center mb-2">
              <Paragraph
                level={5}
                variant="bold"
                text={t('profile.designatedRepresentative.purposeOfDisclosure')}
                className="my-2"
              />
              <Button
                onClick={() => { setIsHelpModalOpen(true) }}
                className="ms-1 svg-18"
                variant="icon_btn"
                data-testid={`PurposeHelpIcon`}
                iconRight={<HelpIcon />}
              />
            </div>
            <div className="col-12">
              <div className="d-flex row align-items-top">
                {formDataPurpose.map((field) => (
                  <div className={field.colClass} key={field.id}>
                    <Controller
                      name={`purpose.${field.id}` as keyof FormDataProps}
                      control={control}
                      rules={{
                        required: field.id === 'disclosureOtherText'
                          ? purposeOptions.disclosureOther ? t('common.requiredField', { fieldName: field.label.toLowerCase() }) : false
                          : !Object.values(purposeOptions).some((value) => value) ? t('common.requiredSelection', { fieldName: t('profile.designatedRepresentative.purposeOfDisclosure').toLowerCase() }) : undefined,
                        validate: () => {
                          const hasSelection = purposeOptions.disclosureRequest || purposeOptions.disclosureOther;
                          return hasSelection || t('common.requiredSelection', { fieldName: t('profile.designatedRepresentative.purposeOfDisclosure').toLowerCase() });
                        }
                      }}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <>
                          {field.id === 'disclosureOtherText' ?
                            (
                              purposeOptions.disclosureOther ? (
                                <div className="offset-xl-6 flex-1 col-md-6 col-12 csrProxy-cust-3">
                                  <TextArea
                                    id={field.id}
                                    rows={3}
                                    value={(value as string) ?? ''}
                                    placeholder={t('common.placeholder', { fieldName: field.label.toLowerCase() })}
                                    maxLength={field.maxLength}
                                    data-testid={`${field.id}Input`}
                                    className={`form-input ${errors.purpose?.[field.id as keyof PurposeProps] ? 'error-border' : ''}`}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                      setValue(`purpose.${field.id}` as keyof FormDataProps, e.target.value, {
                                        shouldValidate: true,
                                      });
                                      clearErrors(`purpose.${field.id}` as keyof FormDataProps);
                                    }}
                                  />
                                </div>
                              ) : null
                            )
                            : (
                              <Checkbox
                                {...field}
                                level={3}
                                id={field.id}
                                type="radio"
                                data-testid={`${field.id}Checkbox`}
                                checked={value || false}
                                onBlur={onBlur}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const isChecked = (e.target as HTMLInputElement).checked;
                                  onChange(isChecked);
                                  if (isChecked) {
                                    formDataPurpose.forEach((option) => {
                                      if (option.id !== field.id && option.id !== 'disclosureOtherText') {
                                        setValue(`purpose.${option.id}` as keyof FormDataProps, false as any);
                                      }
                                    });
                                  }
                                  clearErrors('purpose');
                                  unregister(`purpose.disclosureOtherText` as keyof FormDataProps);
                                }}                              >
                                {field.label}
                              </Checkbox>
                            )}
                        </>
                      )}
                    />
                  </div>
                ))}
                {errors.purpose || edgeCaseForPurpose ? (
                  <div className={`col-12 ${purposeOptions.disclosureOther ? 'offset-xl-6 col-xl-6 csrProxy-cust-4' : 'mt-1'}`}>
                    <ErrorMessage
                      data-testid={`purposeError`}
                      errors={errors}>
                      {errors.purpose?.disclosureOtherText?.message ? errors.purpose.disclosureOtherText.message : edgeCaseForPurpose ? t('common.requiredSelection', { fieldName: t('profile.designatedRepresentative.purposeOfDisclosure').toLowerCase() }) : ''}
                    </ErrorMessage>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <hr className="my-4" />
          {/* authorization section */}
          <div className="row mb-md-4 mb-3">
            <div className="col-12">
              <Paragraph
                level={5}
                variant="bold"
                text={t('common.authorizations')}
                className="mt-md-2"
              />
              <Paragraph
                level={3}
                variant="bold"
                text={t('profile.designatedRepresentative.authorizationsSubHeading')}
                className="mt-3"
              />
            </div>
            <div className="col-12">
              <div className="d-flex row align-items-top">
                {formDataAbuseAuthorization.map((field) => (
                  <div className={field.colClass} key={field.id}>
                    <Controller
                      name={`abuseAuthorization.${field.id}` as keyof FormDataProps}
                      control={control}
                      rules={{
                        validate: () => {
                          const currentAuthorization = getValues('abuseAuthorization') as AbuseAuthorizationProps;
                          const hasSelection = Object.values(currentAuthorization).some((value) => value);
                          return hasSelection || t('common.radioRequired', { fieldName: t('common.authorizations').toLowerCase() });
                        },
                      }}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <Checkbox
                          {...field}
                          level={3}
                          id={field.id}
                          type="radio"
                          data-testid={`${field.id}Checkbox`}
                          checked={value || false}
                          onBlur={onBlur}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const isChecked = (e.target as HTMLInputElement).checked;
                            onChange(isChecked);
                            if (isChecked) {
                              resetAbuseAuthorizationOptions(field.id);
                            }
                            if (isChecked) {
                              clearErrors('abuseAuthorization');
                            }
                          }}
                        > {field.label}</Checkbox>
                      )} />
                  </div>
                ))}
                {errors.abuseAuthorization ? (
                  <div className="col-12 mt-1">
                    <ErrorMessage
                      data-testid="abuseAuthorizationError"
                      errors={errors}
                    >
                      {t('profile.designatedRepresentative.substanceUseRecordsRequired', { fieldName: t('common.authorizations').toLowerCase() })}
                    </ErrorMessage>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="row mt-n2 mb-5">
            <div className="col-12">
              <Paragraph
                level={3}
                variant="bold"
                text={t('profile.designatedRepresentative.acknowledgementPopupTitle')}
              />
              <div className="d-flex justify-content-start">
                <Checkbox
                  id="acknowledgement"
                  level={3}
                  data-testid="acknowledgementCheckbox"
                  checked={acknowledgement}
                  {...register('acknowledgement' as keyof FormDataProps, {
                    required: t('profile.designatedRepresentative.acknowledgementRequired', { fieldName: t('common.acknowledgementAndRights').toLowerCase() }),
                    onChange: (e) => {
                      clearErrors('acknowledgement');
                      setValue('acknowledgement' as keyof FormDataProps, e.target.checked);
                    }
                  })}
                ><Trans
                    i18nKey="common.checkboxAgree"
                    values={{ fieldName: t('common.acknowledgementAndRights') }}
                    components={{
                      link1: (
                        <Link
                          onClick={() => setIsAcknowledgementModal(true)}
                          className="text-decoration-underline text-nowrap"
                          data-testid="acknowledgementCheckboxLink"
                        />
                      )
                    }}
                  /></Checkbox>
              </div>
              {errors.acknowledgement ? (
                <div className={`col-12 mt-3`}>
                  <ErrorMessage
                    data-testid={`acknowledgementError`}
                    errors={errors}
                  >
                    {errors.acknowledgement.message}
                  </ErrorMessage>
                </div>
              ) : null}
            </div>
          </div>
          {isMobile ? <hr className="mt-3 mb-5" /> : null}
          {/* form action buttons */}
          <div className="row mb-3 not-print">
            <div className="col-md-auto col-12 mb-4">
              <Button
                variant="primary_btn"
                type="submit"
                data-testid="submitButton"
                className="btn-md-width"
                disabled={isSubmitting}
              >
                {t('common.submit')}
              </Button>
            </div>
            <div className="col-md-auto col-12 mb-4">
              <Button
                variant="secondary_btn"
                type="button"
                onClick={handleCancel}
                data-testid="cancelButton"
                className="btn-md-width"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </form >
      </div >
      {/*modals*/}
      {Object.keys(formDataModals).map((key) => {
        const modal = formDataModals[key as keyof typeof formDataModals];
        return (
          <ModalMultiUse
            key={key}
            iconClose={modal.iconClose}
            header={modal.title}
            body={modal.content}
            variant={modal.variant}
            isMobile={modal.isMobile}
            isOpen={modal.actionOpen()}
            onClose={modal.actionClose}
            data-testid={modal.id}
          />);
      })}
      {showErrorModal && (
        <ModalMultiUse
          isOpen={showErrorModal}
          onClose={closeErrorModal}
          btnClose={true}
          header={errorModalTitle}
          variant="defaultNoPyBody"
          body={(
            <Paragraph
              text={errorModalMessage}
              variant="regular"
              className="lineBrake mb-0"
            />
          )}
          footer={(
            <div className="col-12 px-0">
              <Button
                dataTestId={'csrProxyErrorModalOkBtn'}
                variant="primary_btn"
                className="btn-md-height w-100"
                onClick={closeErrorModal}
              >
                {t('common.btnOk')}
              </Button>
            </div>
          )}
          data-testid="csrProxyErrorModal"
        />
      )}
    </>
  );
};

export default CSRProxyForm;

